<template>
  <v-app>
    <!-- App bar superior con menú hamburguesa en móvil -->
    <v-app-bar color="#2d2d2d" flat>
      <v-progress-linear
        :active="loading.isLoading.value"
        indeterminate
        color="yellow-darken-2"
        absolute
        bottom
      ></v-progress-linear>
      <v-app-bar-nav-icon @click="drawer = !drawer" class="d-md-and-up-none" />
      <v-toolbar-title>
        <img :src="logoMilys" alt="Logo" class="main-logo" />
        Mily's Beauty Salon
      </v-toolbar-title>
      <v-spacer />
      <!-- Usuario actual a la derecha -->
      <div class="user-info d-flex align-center" style="padding-right: 20px">
        <v-avatar color="secondary" size="32" class="mr-2">
          <span v-if="!isClient" class="white--text">{{ username && username.length > 0 ? username[0] : "?" }}</span>
          <span v-else class="white--text">C</span>
        </v-avatar>
        <span v-if="!isClient" class="user-name">{{ username }}</span>
        <span v-else class="user-name">{{ t('sidebar.clients') }}</span>
      </div>
    </v-app-bar>
    <!-- Sidebar -->
    <v-navigation-drawer
      v-model="drawer"
      :permanent="isDesktop"
      :temporary="!isDesktop"
      color="#2d2d2d"
      elevation="2"
      app
    >
      <template v-for="item in filteredMenu" :key="item.titleKey">
        <v-list-item
          :to="item.route"
          :active="$route.path === item.route"
          :prepend-icon="item.icon"
          :title="item.title"
          link
        />
      </template>

      <v-divider class="my-2" />
      <!-- Agrega esto antes del v-list-item de "Cambiar contraseña" -->
      <div class="lang-toggle-container">
        <span :class="{ 'lang-label-active': selectedLang === 'es' }">ES</span>
        <div class="lang-toggle" @click="toggleLang">
          <div
            class="lang-toggle-knob"
            :class="selectedLang === 'en' ? 'lang-toggle-right' : 'lang-toggle-left'"
          >
            <img
              :src="selectedLang === 'en' ? require('@/assets/us.png') : require('@/assets/mx.png')"
              class="lang-flag"
              alt="Flag"
            />
          </div>
        </div>
        <span :class="{ 'lang-label-active': selectedLang === 'en' }">EN</span>
      </div>
      <v-divider v-if="authStore.user?.username !== 'administrador' && !isClient" class="my-2" />
      <v-list-item
        v-if="authStore.user?.username !== 'administrador' && !isClient"
        @click="showChangePassword = true"
        link
        prepend-icon="mdi-lock-reset"
        :title="t('sidebar.changePassword')"
      />
      <v-divider class="my-2" />
      <!-- Cerrar sesión -->
      <v-list-item @click="logout" link prepend-icon="mdi-logout" :title="t('sidebar.logout')" />
      <v-divider class="my-2" />
    </v-navigation-drawer>
    <!-- Contenido principal -->
    <v-main style="background-color: #232323">
      <v-container fluid>
        <router-view />
      </v-container>
    </v-main>
    <v-snackbar
      v-model="snackbar.show.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
      location="top"
    >
      {{ snackbar.text.value }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show.value = false">
          {{ t('close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="confirmDialog.isVisible.value" persistent max-width="400">
      <v-card class="password-card">
        <v-card-title class="password-gold">{{ confirmDialog.title.value }}</v-card-title>
        <v-card-text class="pt-4">
          <p>{{ confirmDialog.message.value }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog.onCancel()">{{ t('confirm.cancelBtn') }}</v-btn>
          <v-btn class="password-btn" @click="confirmDialog.onConfirm()">{{ t('confirm.confirmBtn') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showChangePassword" persistent max-width="400">
      <v-card class="password-card">
        <v-card-title class="password-gold">{{ t('sidebar.changePassword') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="currentPassword"
              :label="t('sidebar.currentPassword')"
              type="password"
              prepend-icon="mdi-lock"
              style="color: #d6b16d"
            />
            <v-text-field
              v-model="newPassword"
              :label="t('sidebar.newPassword')"
              type="password"
              prepend-icon="mdi-lock"
              style="color: #d6b16d"
            />
            <v-text-field
              v-model="confirmPassword"
              :label="t('sidebar.confirmNewPassword')"
              type="password"
              prepend-icon="mdi-lock"
              style="color: #d6b16d"
            />
            <div v-if="changeError" class="login-error mt-2">{{ changeError }}</div>
            <div v-if="changeSuccess" class="text-success mt-2">{{ changeSuccess }}</div>
            <div class="div-botones">
              <v-btn
                color="primary"
                class="password-btn"
                @click="submitChangePassword"
                >{{ t('save') }}</v-btn
              >
              <v-btn color="primary" class="password-btn" text @click="closeChangePassword">{{ t('cancel') }}</v-btn>

            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watchEffect, computed } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import apiClient from "@/services/api";
import { useI18n } from 'vue-i18n';
import { useSnackbar } from '@/composables/useSnackbar';
import { useConfirm } from '@/composables/useConfirm';
import { useLoading } from '@/composables/useLoading';

const { t, locale } = useI18n();
const selectedLang = ref(locale.value);
const snackbar = useSnackbar();
const confirmDialog = useConfirm();
const loading = useLoading();

import logoMilys from "@/assets/logo-milys_peque.png";
const drawer = ref(true);
const $route = useRoute();
// const router = useRouter();

const showChangePassword = ref(false);
const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const changeError = ref("");
const changeSuccess = ref("");

const authStore = useAuthStore();

// Es mejor usar `computed` para que estas propiedades sean reactivas a los cambios en el store (ej. login/logout)
const isAdmin = computed(() => authStore.user?.rol === "Admin");
const isClient = computed(() => authStore.user?.id === 2); // Considerar usar un rol en vez de un ID hardcodeado si es posible
const username = computed(() => authStore.user?.nombre);

const rawMenu = [
  { titleKey: "sidebar.dashboard", route: "/dashboard", icon: "mdi-view-dashboard", adminRequired: false },
  { titleKey: "sidebar.users", route: "/users", icon: "mdi-account-group", adminRequired: true },
  { titleKey: "sidebar.clients", route: "/clients", icon: "mdi-account-multiple", adminRequired: true },
  { titleKey: "sidebar.services", route: "/services", icon: "mdi-content-cut", adminRequired: true },
  { titleKey: "sidebar.queue", route: "/queue", icon: "mdi-account-clock", adminRequired: false },
  { titleKey: "queueHistory.title", route: "/queue-history", icon: "mdi-history", adminRequired: true },
  { titleKey: "sidebar.attention", route: "/attentionlist", icon: "mdi-hair-dryer", adminRequired: true },
  { titleKey: "sidebar.analytics", route: "/analytics", icon: "mdi-chart-bar", adminRequired: true },
];
const menu = computed(() =>
  rawMenu.map(item => ({
    ...item,
    title: t(item.titleKey)
  }))
);

const filteredMenu = computed(() => {
  if (isClient.value) {
    return [];
  }
  return menu.value.filter(item => !item.adminRequired || isAdmin.value);
});

const isDesktop = ref(window.innerWidth >= 960);
function handleResize() {
  isDesktop.value = window.innerWidth >= 960;
}

function closeChangePassword() {
  showChangePassword.value = false;
  currentPassword.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  changeError.value = "";
  changeSuccess.value = "";
}

function toggleLang() {
  selectedLang.value = selectedLang.value === 'es' ? 'en' : 'es';
  locale.value = selectedLang.value;
  localStorage.setItem('lang', selectedLang.value);
}

async function submitChangePassword() {
  changeError.value = "";
  changeSuccess.value = "";
  // Validaciones simples
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    changeError.value = t('sidebar.fillAllFields');
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    changeError.value = t('sidebar.passwordMismatch');
    return;
  }
  if (newPassword.value.length < 6) {
    changeError.value =  t('sidebar.newPasswordLengthError');
    return;
  }

  try {

    await apiClient.post(`users.php`, {
        user_id: authStore.user.id,
        username: authStore.user.username,
        password: currentPassword.value,
        new_password: newPassword.value,
        tipo: "user_change_password",
      });

      changeSuccess.value =  t('sidebar.passwordChangedSuccess');
      setTimeout(closeChangePassword, 1500);
  } catch (error) {
    // Usar optional chaining para un manejo de errores más conciso
    changeError.value = error.response?.data?.error || t('sidebar.serverError');
  }
}

watchEffect(() => {
  drawer.value = isDesktop.value;
});
onMounted(() => {
  if (isClient.value) {
    drawer.value = false;
  }
  window.addEventListener("resize", handleResize);
  const lang = localStorage.getItem('lang');
  if (lang) {
    selectedLang.value = lang;
    locale.value = lang;
  }

});
onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

function logout() {
  // Aquí va la lógica real de logout
  authStore.logout();
  // router.push("/login");
}
</script>

<style scoped>
.main-logo {
  max-height: 55px;
  margin-right: 10px;
  vertical-align: middle;
}
.v-navigation-drawer {
  border-right: 1px solid #232323;
}
.v-list-item--active {
  background: linear-gradient(90deg, #d6b16d 0%, #b2935e 100%);
  color: #232323 !important;
}
.v-list-item-title {
  font-weight: 500;
}

.password-btn {
  background: linear-gradient(90deg, #d6b16d 0%, #b2935e 100%);
  color: #232323 !important;
  font-weight: bold;
  letter-spacing: 0.5px;
  border-radius: 8px;
}

.div-botones {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}

.password-card {
  background: #2d2d2d;
  border-radius: 16px;
  color: #fff;
}

.password-gold {
  color: #d6b16d !important;
  font-size: 1.2rem !important;
  font-weight: bold;
}

.lang-toggle-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 8px 16px;
}

.lang-label-active {
  color: #d6b16d;
  font-weight: bold;
}

.lang-toggle {
  background: #232323;
  border-radius: 20px;
  width: 56px;
  height: 32px;
  position: relative;
  cursor: pointer;
  box-shadow: 0 2px 8px #0002;
  display: flex;
  align-items: center;
}

.lang-toggle-knob {
  position: absolute;
  top: 2px;
  transition: left 0.25s;
  background: #fff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  box-shadow: 0 1px 4px #0002;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lang-toggle-left {
  left: 2px;
}

.lang-toggle-right {
  left: 26px;
}

.lang-flag {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

</style>
